'use client';

import type { ListItemProps as MuiListItemProps } from '@mui/material/ListItem';
import type { Theme } from 'src/Theme/Theme';
import type React from 'react';
import { default as MuiListItem } from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import { styled } from '@mui/material/styles';
import { shouldForwardProp } from 'src/components/Particles/Particles';
import { MouseEventHandler } from 'react';
type ListItemProps = {
  children: React.ReactNode;
  isActive?: boolean;
  expandable?: boolean;
  handleExpand?: () => void;
} & MuiListItemProps;
const iconMixin = ({
  theme
}: {
  theme: Theme;
}) => ({
  '& .MuiSvgIcon-root': {
    color: theme.colors.icon.tertiary
  },
  '&.active': {
    '&:not(.child)': {
      borderRadius: theme.sizes.radius.sm,
      backgroundColor: theme.colors.surface['brand-active'],
      color: theme.colors.text['brand-dark'],
      '& .MuiTypography-root': {
        color: theme.colors.text['brand-dark']
      },
      '& .MuiSvgIcon-root': {
        color: theme.colors.text['brand-dark']
      },
      '&.child': {
        borderRadius: theme.sizes.radius.sm,
        '& .MuiTypography-root': {
          color: theme.colors.text['brand-dark']
        }
      }
    }
  },
  '&:not(.active):hover': {
    color: theme.colors.text['brand-dark'],
    '& .MuiSvgIcon-root ': {
      color: theme.colors.text.light
    }
  }
});
const StyledListItem = styled(MuiListItem, {
  ...shouldForwardProp(['expandable', 'handleExpand', 'isExpanded', 'isActive'])
})<MuiListItemProps>(({
  theme
}) => ({
  display: 'flex',
  alignItems: 'flex-start',
  alignSelf: 'stretch',
  width: '100%',
  padding: theme.sizes.spacing.none,
  marginBottom: theme.sizes.spacing['02'],
  ...iconMixin({
    theme
  }),
  '&.child': {
    borderLeft: `1px solid ${theme.colors.border.hover}`,
    marginLeft: theme.sizes.spacing['04'],
    paddingLeft: theme.sizes.spacing['04'],
    marginBottom: '0'
  }
}));
const StyledListItemButton = styled(ListItemButton)(({
  theme
}) => ({
  borderRadius: theme.sizes.radius.sm,
  display: 'flex',
  alignItems: 'center',
  gap: theme.sizes.spacing['04'],
  flex: '1 0 0',
  padding: theme.sizes.spacing['03'],
  overflow: 'hidden',
  ...iconMixin({
    theme
  }),
  '& .MuiTypography-root': {
    color: theme.colors.icon.tertiary,
    fontFamily: theme.fonts.family.headline,
    fontSize: theme.fonts.size.md,
    fontStyle: 'normal',
    fontWeight: theme.fonts.weight.medium,
    lineHeight: theme.fonts.lineHeight['leading-5'],
    letterSpacing: theme.fonts.letterSpacing.wide
  },
  '&.active': {
    '&:not(.child)': {
      backgroundColor: theme.colors.surface['brand-active'],
      color: theme.colors.text['brand-dark'],
      '& .MuiTypography-root': {
        color: theme.colors.text['brand-dark']
      }
    },
    '&.child': {
      '& .MuiTypography-root': {
        color: theme.colors.text['brand-dark']
      }
    }
  },
  '&:not(.active):hover': {
    color: theme.colors.text['brand-dark'],
    backgroundColor: '#21201E',
    // TODO: add it from Theme
    '& .MuiTypography-root ': {
      color: theme.colors.text.light
    }
  }
}));
const ListItem: React.FC<ListItemProps> = ({
  children,
  expandable = false,
  handleExpand = () => void 0,
  className,
  ...props
}) => {
  const handleClick: MouseEventHandler<HTMLDivElement | HTMLLIElement> = e => {
    if (expandable) {
      e.stopPropagation();
      handleExpand();
    }
  };
  return <StyledListItem disablePadding className={className} {...props}>
      <StyledListItemButton disableRipple onClick={handleClick} className={className}>
        {children}
      </StyledListItemButton>
    </StyledListItem>;
};
export default ListItem;
export type { ListItemProps };